import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { CreditCard, FileText, ChevronDown } from "lucide-react";
import { toast } from "sonner";
import { API, fmtErr, money, fmtDate } from "../lib/api";
import { useLang } from "../lib/i18n";
import { useAuth, isManagerUp } from "../lib/auth";
import { StatusBadge } from "./StatusBadge";
import { Button } from "./ui/button";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "./ui/dialog";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "./ui/table";

export function CustomerDetail({ customerId, open, onClose }) {
  const { t } = useLang();
  const { user } = useAuth();
  const managerUp = isManagerUp(user);
  const navigate = useNavigate();
  const [data, setData] = useState(null);
  const [busy, setBusy] = useState(false);
  const [expanded, setExpanded] = useState(null);

  const load = () => {
    if (!customerId) return;
    API.get(`/shop/customers/${customerId}/detail`).then((r) => setData(r.data)).catch(() => {});
  };
  useEffect(() => { setData(null); load(); /* eslint-disable-next-line */ }, [customerId]);

  const issueCard = async () => {
    setBusy(true);
    try {
      await API.post(`/shop/customers/${customerId}/issue-card`);
      toast.success(t("cust.card_issued"));
      load();
    } catch (err) {
      toast.error(fmtErr(err));
    } finally {
      setBusy(false);
    }
  };

  const c = data?.customer;
  const s = data?.summary;
  const info = [
    [t("customers.id_type"), c?.id_type], [t("customers.id_number"), c?.id_number],
    [t("common.phone"), c?.phone], [t("customers.email"), c?.email],
    [t("common.address"), c?.address], [t("cust.municipality"), c?.municipality],
    [t("cust.posto"), c?.posto], [t("cust.suco"), c?.suco],
    [t("cust.aldeia"), c?.aldeia],
  ];
  const cards = [
    ["cust.total_paid", s?.total_paid, "bg-emerald-50 border-emerald-200 text-emerald-800", "sum-total-paid"],
    ["cust.full_closeout", s?.full_total, "bg-slate-50 border-slate-200 text-slate-800", "sum-full"],
    ["cust.partial", s?.partial_total, "bg-blue-50 border-blue-200 text-blue-800", "sum-partial"],
    ["repay.interest_part", s?.interest_total, "bg-amber-50 border-amber-200 text-amber-800", "sum-interest"],
    ["fin.penalty", s?.penalty_total, "bg-red-50 border-red-200 text-red-700", "sum-penalty"],
  ];

  return (
    <Dialog open={open} onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="bg-white max-w-3xl max-h-[90vh] overflow-y-auto" data-testid="customer-detail-dialog">
        <DialogHeader>
          <DialogTitle className="font-heading">{c ? `${c.name} · ${t("cust.detail_title")}` : t("common.loading")}</DialogTitle>
        </DialogHeader>
        {data && c && (
          <div className="space-y-6">
            <div className="flex gap-5">
              <div className="h-24 w-24 shrink-0 rounded-xl bg-slate-100 border border-slate-200 flex items-center justify-center overflow-hidden font-heading font-bold text-2xl text-slate-500" data-testid="cust-avatar">
                {c.photo_path ? (
                  <img src={`${API.defaults.baseURL}/shop/customers/${c.id}/photo`} alt={c.name} className="h-full w-full object-cover" />
                ) : (
                  c.name.split(/\s+/).map((w) => w[0]).slice(0, 2).join("")
                )}
              </div>
              <div className="grid grid-cols-2 gap-x-8 gap-y-2 flex-1">
                {info.map(([l, v], i) => (
                  <div key={i}>
                    <div className="text-[10px] uppercase tracking-wider text-slate-400">{l}</div>
                    <div className="text-sm text-slate-800">{v || "—"}</div>
                  </div>
                ))}
              </div>
            </div>

            <div className="border border-slate-200 rounded-lg p-4 flex items-center justify-between" data-testid="member-card-panel">
              <div className="flex items-center gap-3">
                <div className="h-9 w-9 rounded-lg bg-[#1E3A5F] flex items-center justify-center">
                  <CreditCard className="h-4 w-4 text-white" />
                </div>
                <div>
                  <div className="text-[10px] uppercase tracking-wider text-slate-500 font-semibold">{t("cust.member_card")}</div>
                  <div className="text-sm font-mono font-semibold" data-testid="member-card-no">
                    {c.member_card_no || t("cust.no_card")}
                  </div>
                  {c.card_issued_at && <div className="text-xs text-slate-400">{fmtDate(c.card_issued_at)}</div>}
                </div>
              </div>
              {managerUp && !c.member_card_no && (
                <Button size="sm" data-testid="issue-card-button" onClick={issueCard} disabled={busy}
                  className="bg-[#1E3A5F] hover:bg-[#16294a] text-white">
                  <CreditCard className="h-4 w-4 mr-1.5" />{t("cust.issue_card")}
                </Button>
              )}
            </div>

            <div>
              <div className="text-[11px] uppercase tracking-wider text-slate-500 font-semibold mb-2">
                {t("cust.contracts")} ({data.loans.length})
              </div>
              <div className="border border-slate-200 rounded-lg overflow-hidden">
                <Table>
                  <TableHeader>
                    <TableRow>
                      <TableHead>{t("rpt.contract_no")}</TableHead>
                      <TableHead>{t("rpt.item")}</TableHead>
                      <TableHead>{t("rpt.loan_amount")}</TableHead>
                      <TableHead>{t("cust.remaining")}</TableHead>
                      <TableHead>{t("rpt.due_date")}</TableHead>
                      <TableHead>{t("common.status")}</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {data.loans.length === 0 && (
                      <TableRow><TableCell colSpan={6} className="text-center text-slate-500 py-6">{t("common.none")}</TableCell></TableRow>
                    )}
                    {data.loans.map((l) => (
                      <TableRow key={l.id} data-testid={`custd-contract-${l.loan_number}`}>
                        <TableCell className="font-mono text-xs">{l.contract_no}</TableCell>
                        <TableCell className="text-sm">{l.item}</TableCell>
                        <TableCell className="font-mono">{money(l.principal)}</TableCell>
                        <TableCell className="font-mono">{money(l.remaining)}</TableCell>
                        <TableCell className="font-mono text-xs">{l.due_date}</TableCell>
                        <TableCell><StatusBadge status={l.status} /></TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </div>
            </div>

            <div>
              <div className="text-[11px] uppercase tracking-wider text-slate-500 font-semibold mb-2">
                {t("cust.payment_summary")} · {t("cust.payment_history")} ({s.count})
              </div>
              <div className="grid grid-cols-2 sm:grid-cols-5 gap-2 mb-3">
                {cards.map(([k, v, cls, tid]) => (
                  <div key={k} className={`border rounded-lg p-3 ${cls}`} data-testid={tid}>
                    <div className="text-[9px] uppercase tracking-wider font-semibold opacity-70">{t(k)}</div>
                    <div className="font-mono font-bold text-sm">{money(v || 0)}</div>
                  </div>
                ))}
              </div>
              <div className="border border-slate-200 rounded-lg overflow-hidden">
                <Table>
                  <TableHeader>
                    <TableRow>
                      <TableHead>{t("rcp.receipt_col")}</TableHead>
                      <TableHead>{t("rpt.contract_no")}</TableHead>
                      <TableHead>{t("repay.items")}</TableHead>
                      <TableHead>{t("pay.type")}</TableHead>
                      <TableHead>{t("common.amount")}</TableHead>
                      <TableHead>{t("common.date")}</TableHead>
                      <TableHead className="text-right">PDF</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {data.payments.length === 0 && (
                      <TableRow><TableCell colSpan={7} className="text-center text-slate-500 py-6">{t("common.none")}</TableCell></TableRow>
                    )}
                    {data.payments.map((p) => (
                      <React.Fragment key={p.id}>
                        <TableRow data-testid={`custd-payment-${p.id.slice(0, 8)}`}>
                          <TableCell className="font-mono text-xs">{p.receipt_no}</TableCell>
                          <TableCell className="font-mono text-xs">{p.contract_no}</TableCell>
                          <TableCell>
                            {p.items?.length ? (
                              <button data-testid={`custd-item-toggle-${p.id.slice(0, 8)}`}
                                onClick={() => setExpanded(expanded === p.id ? null : p.id)}
                                className="flex items-center gap-1 text-xs font-medium text-emerald-700 hover:text-emerald-800">
                                {p.items[0].name}{p.items.length > 1 ? ` +${p.items.length - 1}` : ""}
                                <ChevronDown className={`h-3 w-3 transition-transform ${expanded === p.id ? "rotate-180" : ""}`} />
                              </button>
                            ) : "—"}
                          </TableCell>
                          <TableCell className="text-sm">{p.payment_type ? t(`pay.type_${p.payment_type}`) : "—"}</TableCell>
                          <TableCell className="font-mono">{money(p.amount)}</TableCell>
                          <TableCell className="font-mono text-xs">{p.date}</TableCell>
                          <TableCell className="text-right">
                            <button data-testid={`custd-receipt-${p.id.slice(0, 8)}`}
                              onClick={() => { onClose(); navigate(`/app/repayments?open=${p.id}`); }}
                              className="p-1.5 hover:bg-red-50 rounded-lg" title={t("repay.receipt")}>
                              <FileText className="h-4 w-4 text-red-600" />
                            </button>
                          </TableCell>
                        </TableRow>
                        {expanded === p.id && (
                          <TableRow data-testid={`custd-item-detail-${p.id.slice(0, 8)}`}>
                            <TableCell colSpan={7} className="bg-slate-50">
                              <div className="flex flex-wrap gap-2 py-1">
                                {p.items.map((it) => (
                                  <div key={it.id} data-testid={`custd-item-card-${it.id.slice(0, 8)}`}
                                    className="flex items-center gap-3 bg-white border border-slate-200 rounded-lg px-3 py-2">
                                    {it.photo_path && (
                                      <img src={`${API.defaults.baseURL}/shop/items/${it.id}/photo`} alt=""
                                        onError={(e) => { e.currentTarget.style.display = "none"; }}
                                        className="h-10 w-10 rounded object-cover border border-slate-200" />
                                    )}
                                    <div>
                                      <div className="text-sm font-medium text-slate-900">{it.name}</div>
                                      <div className="text-xs text-slate-500">
                                        {it.category}{it.plate_no || it.serial_no ? ` · ${it.plate_no || it.serial_no}` : ""}
                                      </div>
                                      <div className="text-xs font-mono text-slate-600">
                                        {money(it.estimated_value)}{it.location ? ` · ${it.location}` : ""}
                                      </div>
                                    </div>
                                  </div>
                                ))}
                              </div>
                            </TableCell>
                          </TableRow>
                        )}
                      </React.Fragment>
                    ))}
                  </TableBody>
                </Table>
              </div>
            </div>
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
}
